import { useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Button, Card, Col, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../../components/auth/AuthProvider';
import { setOrgToken } from '../../controllers/org';
import { apiBaseUrl } from '../../controllers/config';

function ServiciosEnComunidades({ scope = 'diocesis', scopeId, ciclo }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selectedCiclo, setSelectedCiclo] = useState(ciclo || new Date().getFullYear());

  useEffect(() => {
    setOrgToken(token);
  }, [token]);

  useEffect(() => {
    if (ciclo) {
      setSelectedCiclo(ciclo);
    }
  }, [ciclo]);

  useEffect(() => {
    if (!token) {
      return;
    }

    async function loadData() {
      try {
        setLoading(true);
        const url = new URL(`${apiBaseUrl}/org/servicios-comunidades`, window.location.origin);
        url.searchParams.set('scope', scope);
        if (scopeId) {
          url.searchParams.set('scopeId', scopeId);
        }
        if (selectedCiclo) {
          url.searchParams.set('ciclo', selectedCiclo);
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const payload = await response.json();
        if (!response.ok) {
          throw payload;
        }
        setData(payload);
        setError('');
      } catch (e) {
        setError(e.message || 'No se pudo cargar la información.');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [token, scope, scopeId, selectedCiclo]);

  const comunidades = data?.comunidades || [];
  const servicios = data?.servicios || [];

  function toggleServicio(comunidadId, servicioId) {
    setData((current) => ({
      ...current,
      comunidades: (current?.comunidades || []).map((comunidad) => {
        if (Number(comunidad.comunidad_id) !== Number(comunidadId)) {
          return comunidad;
        }

        return {
          ...comunidad,
          servicios: (comunidad.servicios || []).map((servicio) => {
            if (Number(servicio.id) !== Number(servicioId)) {
              return servicio;
            }
            return { ...servicio, selected: !servicio.selected };
          })
        };
      })
    }));
  }

  async function handleSave() {
    try {
      setSaving(true);
      const response = await fetch(`${apiBaseUrl}/org/servicios-comunidades`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ciclo: selectedCiclo,
          scope,
          scopeId,
          comunidades: (data?.comunidades || []).map((comunidad) => ({
            comunidad_id: comunidad.comunidad_id,
            servicios: (comunidad.servicios || []).filter((servicio) => servicio.selected).map((servicio) => servicio.id)
          }))
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw payload;
      }
      setError('');
      setData((current) => ({ ...current, saved: true }));
    } catch (e) {
      setError(e.message || 'No se pudo guardar la información.');
    } finally {
      setSaving(false);
    }
  }

  const groupedByDiocesis = useMemo(() => {
    if (scope !== 'region' && scope !== 'nacional') {
      return [];
    }

    const grouped = comunidades.reduce((acc, comunidad) => {
      const diocesisId = comunidad.diocesis_id;
      const diocesisLabel = comunidad.diocesis?.label || `Diócesis ${diocesisId}`;
      if (!acc[diocesisId]) {
        acc[diocesisId] = { diocesisId, diocesisLabel, comunidades: [] };
      }
      acc[diocesisId].comunidades.push(comunidad);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.diocesisLabel.localeCompare(b.diocesisLabel));
  }, [scope, comunidades]);

  const groupedByRegion = useMemo(() => {
    if (scope !== 'nacional') {
      return [];
    }

    const grouped = comunidades.reduce((acc, comunidad) => {
      const regionId = comunidad.diocesis?.region?.region_id || comunidad.diocesis?.region_id;
      const regionLabel = comunidad.diocesis?.region?.label || `Región ${regionId}`;
      if (!acc[regionId]) {
        acc[regionId] = { regionId, regionLabel, diocesis: [] };
      }

      const diocesisId = comunidad.diocesis_id;
      const diocesisLabel = comunidad.diocesis?.label || `Diócesis ${diocesisId}`;
      const existingDiocesis = acc[regionId].diocesis.find((item) => Number(item.diocesisId) === Number(diocesisId));
      if (existingDiocesis) {
        existingDiocesis.comunidades.push(comunidad);
      }
      else {
        acc[regionId].diocesis.push({ diocesisId, diocesisLabel, comunidades: [comunidad] });
      }

      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.regionLabel.localeCompare(b.regionLabel));
  }, [scope, comunidades]);

  if (loading) {
    return <div className="py-4"><Spinner animation="border" /></div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <Card className="mt-3">
      <Card.Body>
        <Row className="mb-3 align-items-end">
          <Col md={12} className="text-end">
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </Col>
        </Row>

        {scope === 'nacional' ? (
          <Tabs defaultActiveKey={String(groupedByRegion[0]?.regionId || '')} className="mb-3">
            {groupedByRegion.map((region) => (
              <Tab eventKey={String(region.regionId)} key={region.regionId} title={region.regionLabel}>
                <Accordion alwaysOpen>
                  {region.diocesis.map((diocesis) => (
                    <Accordion.Item eventKey={String(diocesis.diocesisId)} key={diocesis.diocesisId}>
                      <Accordion.Header>{diocesis.diocesisLabel}</Accordion.Header>
                      <Accordion.Body>
                        <div className="mb-3">
                          {diocesis.comunidades.map((comunidad) => (
                            <div key={comunidad.comunidad_id} className="mb-4 border rounded p-3">
                              <h6 className="mb-3">{comunidad.label}</h6>
                              <Row xs={1} md={2} lg={3} className="g-3">
                                {servicios.map((servicio) => (
                                  <Col key={`${comunidad.comunidad_id}-${servicio.id}`}>
                                    <Form.Check
                                      type="checkbox"
                                      label={servicio.label}
                                      checked={Boolean(comunidad.servicios?.find((item) => item.id === servicio.id)?.selected)}
                                      onChange={() => toggleServicio(comunidad.comunidad_id, servicio.id)}
                                    />
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          ))}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Tab>
            ))}
          </Tabs>
        ) : scope === 'region' ? (
          <Accordion alwaysOpen>
            {groupedByDiocesis.map((group) => (
              <Accordion.Item eventKey={String(group.diocesisId)} key={group.diocesisId}>
                <Accordion.Header>{group.diocesisLabel}</Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3">
                    {group.comunidades.map((comunidad) => (
                      <div key={comunidad.comunidad_id} className="mb-4 border rounded p-3">
                        <h6 className="mb-3">{comunidad.label}</h6>
                        <Row xs={1} md={2} lg={3} className="g-3">
                          {servicios.map((servicio) => (
                            <Col key={`${comunidad.comunidad_id}-${servicio.id}`}>
                              <Form.Check
                                type="checkbox"
                                label={servicio.label}
                                checked={Boolean(comunidad.servicios?.find((item) => item.id === servicio.id)?.selected)}
                                onChange={() => toggleServicio(comunidad.comunidad_id, servicio.id)}
                              />
                            </Col>
                          ))}
                        </Row>
                      </div>
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : (
          <div>
            {comunidades.length === 0 ? (
              <Alert variant="info">No hay comunidades para este alcance.</Alert>
            ) : (
              comunidades.map((comunidad) => (
                <Card key={comunidad.comunidad_id} className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">{comunidad.label}</h6>
                    <Row xs={1} md={2} lg={3} className="g-3">
                      {servicios.map((servicio) => (
                        <Col key={`${comunidad.comunidad_id}-${servicio.id}`}>
                          <Form.Check
                            type="checkbox"
                            label={servicio.label}
                            checked={Boolean(comunidad.servicios?.find((item) => item.id === servicio.id)?.selected)}
                            onChange={() => toggleServicio(comunidad.comunidad_id, servicio.id)}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ServiciosEnComunidades;
